import React, { useCallback, useEffect, useRef, useState } from "react";
import {useNavigate} from 'react-router-dom';
import {
  AlertCircle,
  ArrowUp,
  ArrowUpRight,
  Bot,
  Paperclip,
  Calculator,
  Check,
  CheckCircle2,
  Copy,
  Download,
  FileText,
  Layers,
  SquarePen,
  Sliders,
  Sparkles,
  X,
} from "lucide-react";
import { applyUiSync } from "./agentBridge";
import {
  FormattedMessageContent,
  initialAssistantMessage,
  mockupContentPages,
  mockupFormulaRules,
  toolDisplayName,
  normalizeToolOutcomes,
  toolStatusClass,
} from "./agentMessageFormat";
import "./agentAssistant.css";
import { agentRequestOptions, requireAgentReply } from "./agentApi";

const agentFetch = (url, options) => fetch(url, agentRequestOptions(url, options));

const SESSION_KEY = "roi_agent_session_id";
const PRODUCT_NAME = "AI Calculator Assistant";
const MAX_ATTACHMENTS = 3;
const MAX_FILE_BYTES = 2 * 1024 * 1024;
const ACCEPT_FILES = "*/*";

/** Builder digest + mockup loop — allow up to 20 minutes (override via REACT_APP_AGENT_CHAT_TIMEOUT_MS). */
const CHAT_FETCH_TIMEOUT_MS =
  Number(process.env.REACT_APP_AGENT_CHAT_TIMEOUT_MS) || 1200000;

/** CRA .env values sometimes include stray quotes or spaces. */
export function normalizeApiUrl(url) {
  if (!url) return "";
  return String(url)
    .trim()
    .replace(/^['"]+|['"]+$/g, "");
}

async function agentChatFetch(url, options = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CHAT_FETCH_TIMEOUT_MS);
  try {
    const res = await agentFetch(url, { ...options, signal: controller.signal });
    const raw = await res.text();
    let data = {};
    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch {
        throw new Error(
          res.ok
            ? "Server returned invalid JSON."
            : `Server error (${res.status}). Check that the API at ${url} is running.`
        );
      }
    }
    return { res, data };
  } catch (err) {
    if (err.name === "AbortError") {
      throw new Error(
        "This request took longer than expected. Please try again in this chat."
      );
    }
    if (err.message === "Failed to fetch" || err.name === "TypeError") {
      throw new Error(
        "Unable to connect to the assistant. Please try again shortly."
      );
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

function mockupFromChatResponse(data) {
  return data?.approval_ui?.mockup || data?.pending_mockup || null;
}

function sessionHasUserMessages(sessionMessages) {
  return Array.isArray(sessionMessages) && sessionMessages.some((m) => m.role === "user");
}

function messagesFromSession(sessionMessages, mode) {
  const transcript = (sessionMessages || [])
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((m) => ({
      role: m.role,
      content: m.content || "",
      ...(m.user_name ? { user_name: m.user_name } : {}),
      ...(m.attachments?.length ? { attachments: m.attachments } : {}),
      ...(m.tool_calls?.length ? { tool_calls: m.tool_calls } : {}),
    }));
  if (!transcript.length) {
    return [{ role: "assistant", content: initialAssistantMessage(mode) }];
  }
  return transcript;
}

function userInitials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export default function AgentAssistantWidget({
  roiId,
  mode = "runtime",
  apiUrl: apiUrlProp,
  bridgeHandlers = {},
  enabled = true,
  embedded = false,
  defaultOpen = false,
  open: openControlled,
  onOpenChange,
  userName = "Admin",
  onBuildSuccess,
}) {
  const apiUrl = normalizeApiUrl(apiUrlProp);
  const navigate=useNavigate();
  const isControlled = openControlled !== undefined;
  const [openInternal, setOpenInternal] = useState(defaultOpen || embedded);
  const open = isControlled ? openControlled : openInternal;

  const setOpen = useCallback(
    (next) => {
      const value = typeof next === "function" ? next(open) : next;
      if (isControlled) onOpenChange?.(value);
      else setOpenInternal(value);
    },
    [isControlled, onOpenChange, open]
  );

  const [messages, setMessages] = useState([
    { role: "assistant", content: initialAssistantMessage(mode) },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingHint, setLoadingHint] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [pendingMockup, setPendingMockup] = useState(null);
  const [building, setBuilding] = useState(false);
  const [buildError, setBuildError] = useState('');
  const [includeReportView, setIncludeReportView] = useState(true);
  const [includePdfDownload, setIncludePdfDownload] = useState(true);
  const [mockupTab, setMockupTab] = useState("pages");
  const [lastMetrics, setLastMetrics] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const listRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const sessionHydratedRef = useRef(null);
  const panelWasOpenRef = useRef(false);

  useEffect(() => {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) setSessionId(stored);
  }, []);

  useEffect(() => {
    if (!pendingMockup) return;
    setIncludeReportView(pendingMockup.metadata?.include_report_view !== false);
    setIncludePdfDownload(pendingMockup.metadata?.include_pdf_download !== false);
    setMockupTab("pages");
  }, [pendingMockup?.mockup_id]);

  const hydrateSessionFromServer = useCallback(async () => {
    if (!sessionId || !apiUrl) return;
    const res = await agentFetch(`${apiUrl}/roi-agent/conversations/detail/${sessionId}`);
    const data = await res.json();
    const row = data?.data;
    if (!row) {
      sessionStorage.removeItem(SESSION_KEY);
      setSessionId(null);
      setPendingMockup(null);
      return;
    }

    const sessionMessages = row.messages || [];
    const started = sessionHasUserMessages(sessionMessages);

    if (!started) {
      setPendingMockup(null);
      sessionStorage.removeItem(SESSION_KEY);
      setSessionId(null);
      sessionHydratedRef.current = null;
      return;
    }

    let sessionMsgs = messagesFromSession(sessionMessages, mode);
    if (row.pending_mockup_cleared) {
      sessionMsgs = [
        ...sessionMsgs,
        {
          role: "assistant",
          content:
            "The saved approval blueprint had invalid formulas (empty steps or generic Rule names). Ask me to propose the calculator mockup again after we map Excel logic.",
        },
      ];
    }
    setMessages(sessionMsgs);
    if (row.final_metrics) setLastMetrics(row.final_metrics);
    if (row.pending_mockup?.mockup_id && !row.pending_mockup_cleared) {
      setPendingMockup(row.pending_mockup);
      setMockupTab("formulas");
    } else {
      setPendingMockup(null);
    }
    sessionHydratedRef.current = sessionId;
    stickToBottomRef.current = true;
  }, [apiUrl, mode, sessionId]);

  useEffect(() => {
    const panelActive = embedded || open;
    const panelJustOpened = panelActive && !panelWasOpenRef.current;
    panelWasOpenRef.current = panelActive;

    if (!panelActive || !sessionId || !apiUrl) return;

    const shouldHydrate =
      panelJustOpened ||
      (embedded && sessionHydratedRef.current !== sessionId);

    if (!shouldHydrate) return;

    let cancelled = false;
    (async () => {
      try {
        await hydrateSessionFromServer();
      } catch {
        /* ignore */
      }
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [embedded, open, sessionId, apiUrl, hydrateSessionFromServer]);

  const syncMessageScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || !stickToBottomRef.current) return;
    el.scrollTop = messages.some(message=>message.role==='user') ? el.scrollHeight : 0;
  }, [messages]);

  useEffect(() => {
    if (!open && !embedded) return;
    syncMessageScroll();
  }, [messages, open, embedded, loading, pendingMockup, syncMessageScroll]);

  const onMessagesScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const threshold = 72;
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  }, []);

  const adjustTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    el.style.overflowY = el.scrollHeight > 120 ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [input, adjustTextareaHeight]);

  const clearChat = useCallback(() => {
    setMessages([{ role: "assistant", content: initialAssistantMessage(mode) }]);
    setPendingMockup(null);
    setLastMetrics(null);
    setInput("");
    setPendingAttachments([]);
    sessionStorage.removeItem(SESSION_KEY);
    setSessionId(null);
    sessionHydratedRef.current = null;
  }, [mode]);

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    setPendingAttachments((prev) => {
      const next = [...prev];
      for (const file of picked) {
        if (next.length >= MAX_ATTACHMENTS) break;
        if (file.size > MAX_FILE_BYTES) {
          setMessages((m) => [
            ...m,
            {
              role: "assistant",
              content: `File "${file.name}" is too large. Max size is 2MB per file.`,
            },
          ]);
          continue;
        }
        if (next.some((p) => p.name === file.name && p.size === file.size)) continue;
        next.push({ id: `${file.name}-${file.size}-${Date.now()}`, file });
      }
      return next;
    });
  };

  const removePendingFile = (id) => {
    setPendingAttachments((prev) => prev.filter((p) => p.id !== id));
  };

  const copyMessage = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch {
      /* ignore */
    }
  };

  const sendMessage = useCallback(
    async (text) => {
      const userText = text?.trim() || "";
      const filesToSend = pendingAttachments;
      if ((!userText && !filesToSend.length) || !apiUrl) return;

      stickToBottomRef.current = true;

      const attachmentMeta = filesToSend.map((a) => ({
        filename: a.file.name,
        size: a.file.size,
      }));
      const displayText =
        userText ||
        (filesToSend.length === 1
          ? `Uploaded: ${filesToSend[0].file.name}`
          : `Uploaded ${filesToSend.length} files`);

      setMessages((m) => [
        ...m,
        {
          role: "user",
          content: displayText,
          attachments: attachmentMeta.length ? attachmentMeta : undefined,
          user_name: userName,
        },
      ]);
      setInput("");
      setPendingAttachments([]);
      setLoading(true);
      if (filesToSend.length && mode === "builder") {
        const names = filesToSend.map((f) => String(f.file?.name || "").toLowerCase());
        const hasExcel = names.some((n) => /\.(xlsx|xls|xlsm)$/.test(n));
        const hasMockup = names.some((n) => /\.(png|jpe?g|webp|gif|bmp|pdf)$/.test(n));
        if (hasExcel && hasMockup) {
          setLoadingHint("Reading your workbook and design…");
        } else if (hasExcel) {
          setLoadingHint("Reading your workbook…");
        } else if (hasMockup) {
          setLoadingHint("Reviewing your design…");
        } else {
          setLoadingHint("Processing attachments…");
        }
      } else {
        setLoadingHint("");
      }
      try {
        const field_state = bridgeHandlers?.getFieldState?.() || {};
        let res;
        let data;

        if (filesToSend.length) {
          const fd = new FormData();
          fd.append(
            "message",
            userText ||
              "Use the attached logic/requirements material to design or update the calculator."
          );
          if (sessionId) fd.append("session_id", sessionId);
          if (roiId) fd.append("roi_id", roiId);
          fd.append("mode", mode);
          fd.append("user_name", userName);
          fd.append("field_state", JSON.stringify(field_state));
          filesToSend.forEach(({ file }) => fd.append("files", file));
          ({ res, data } = await agentChatFetch(`${apiUrl}/roi-agent/chat`, {
            method: "POST",
            body: fd,
          }));
        } else {
          ({ res, data } = await agentChatFetch(`${apiUrl}/roi-agent/chat`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              message: userText,
              session_id: sessionId,
              roi_id: roiId || undefined,
              mode,
              field_state,
              user_name: userName,
            }),
          }));
        }

        requireAgentReply(res, data);

        if (data.session_id) {
          sessionStorage.setItem(SESSION_KEY, data.session_id);
          setSessionId(data.session_id);
          sessionHydratedRef.current = data.session_id;
        }
        if (data.ui_sync?.length) {
          applyUiSync(data.ui_sync, {...bridgeHandlers,navigate});
        }
        if (data.metrics) setLastMetrics(data.metrics);
        const nextMockup = mockupFromChatResponse(data);
        if (nextMockup) setPendingMockup(nextMockup);
        else if (data.pending_mockup) setPendingMockup(data.pending_mockup);

        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content: data.reply,
            tool_calls: data.tool_calls,
            tool_outcomes: data.tool_outcomes,
          },
        ]);
      } catch (err) {
        setMessages((m) => [
          ...m,
          { role: "assistant", content: `Sorry, something went wrong: ${err.message}` },
        ]);
      } finally {
        setLoading(false);
        setLoadingHint("");
      }
    },
    [apiUrl, sessionId, roiId, mode, bridgeHandlers, navigate, userName, pendingAttachments]
  );

  const approveBuild = async (approved) => {
    if (!pendingMockup?.mockup_id || !sessionId) {setBuildError('The draft session is missing. Reload this conversation before building.');return;}
    setLoading(true);
    setBuilding(approved);
    setBuildError('');
    setLoadingHint(approved?(pendingMockup.edit?'Saving calculator changes…':'Creating your calculator…'):'Cancelling draft…');
    try {
      const res = await agentFetch(`${apiUrl}/roi-agent/approve-build`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          mockup_id: pendingMockup.mockup_id,
          approved,
          user_name: userName,
          include_report_view: includeReportView,
          include_pdf_download: includePdfDownload,
        }),
      });
      const data = await res.json();
      if(!res.ok)throw Error(data.error || data.reply || `Build request failed (${res.status})`);
      if(approved && data.build?.result!=='success')setBuildError(data.reply || data.build?.message || 'Build failed. Review the QA issues above.');
      setPendingMockup(data.build?.result === "success" || !approved ? null : data.pending_mockup || pendingMockup);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: data.reply || JSON.stringify(data) },
      ]);
      if (approved && data.build?.result === "success") {
        onBuildSuccess?.(data.build);
      }
    } catch (err) {
      setBuildError(err.message);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: `Build failed: ${err.message}` },
      ]);
    } finally {
      setLoading(false);
      setBuilding(false);
      setLoadingHint('');
    }
  };
  const verifyDraft = async () => {
    if(!sessionId || !pendingMockup?.mockup_id) return;
    setLoading(true);
    setLoadingHint('Checking your calculator…');
    try {
      const res=await agentFetch(`${apiUrl}/roi-agent/verify-draft`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({session_id:sessionId,mockup_id:pendingMockup.mockup_id})});
      const data=await res.json();
      if(!res.ok)throw Error(data.error||'QA request failed');
      if(data.pending_mockup)setPendingMockup(data.pending_mockup);
      setMessages(m=>[...m,{role:'assistant',content:data.reply}]);
    }catch(error){setMessages(m=>[...m,{role:'assistant',content:error.message}]);}
    finally{setLoading(false);setLoadingHint('');}
  };

  const quickChips =
    mode === "builder"
      ? [
          {
            label: "Calculate ROI",
            text: "Help me create an ROI calculator. Ask what I want to measure, then propose the inputs, formulas, and assumptions for my review.",
          },
          {
            label: "Compare costs",
            text: "Help me create a cost comparison calculator. Let's define what we are comparing and agree on the calculation logic.",
          },
          { label: "Start with my idea", text: "I'd like to create a calculator. Help me turn my idea into inputs, formulas, and a working calculator." },
        ]
      : [
          { label: "Explain ROI", text: "Explain the ROI breakdown" },
          { label: "Calculate", text: "Calculate ROI metrics with current inputs" },
          { label: "Reset", text: "Reset calculator to defaults" },
        ];

  const contentPages = pendingMockup ? mockupContentPages(pendingMockup) : [];
  const formulaRules = pendingMockup ? mockupFormulaRules(pendingMockup) : [];

  const previewPages = (() => {
    if (!pendingMockup) return [];
    const base = contentPages;
    const pages = [...base];
    if (
      includeReportView &&
      !pages.some((p) => p.template_type_id === 6 || p.page_name === "Savings Report")
    ) {
      pages.push({ page_name: "Savings Report", optional: true });
    }
    if (
      includePdfDownload &&
      !pages.some((p) => p.page_name === "Download PDF" || p.delivery_kind === "pdf_download")
    ) {
      pages.push({ page_name: "Download PDF", optional: true });
    }
    return pages;
  })();

  const renderApprovalCard = () => {
    if (!pendingMockup) return null;
    const pages = previewPages;
    const tabConfig = [
      { id: "pages", label: "Pages", Icon: Layers },
      { id: "fields", label: "Fields", Icon: Sliders },
      { id: "formulas", label: "Formulas", Icon: Calculator },
    ];

    return (
      <div className="roi-agent-msg-row assistant">
        <div className="roi-agent-avatar-sm">
          <Bot size={14} />
        </div>
        <div className="roi-agent-msg-col">
          <div className="roi-agent-approval-card">
            <div className="roi-agent-approval-head">
              <div className="roi-agent-approval-icon">
                <Sparkles size={18} />
              </div>
              <div>
                <p className="roi-agent-approval-title">{pendingMockup.verification?.passed ? "Calculator draft verified" : pendingMockup.verification?.logic_confirmation?.required ? "Confirm calculation logic" : "Calculator draft needs QA"}</p>
                <p className="roi-agent-approval-sub">
                  {pendingMockup.metadata?.roi_name || "AI Generated Calculator"}
                </p>
              </div>
            </div>
            <div className="roi-agent-approval-sub" role="status">
              {pendingMockup.verification?.passed ? "Required checks passed." : pendingMockup.verification?.logic_confirmation?.required ? "Review the proposed inputs, formulas, and assumptions in the chat, then confirm the logic." : "Approval is available after QA passes."}
              {[...(pendingMockup.verification?.numerical?.issues || []), ...(pendingMockup.verification?.visual?.issues || []), ...(pendingMockup.verification?.issues || [])].slice(0, 8).map((issue, index) => <p key={index}>{issue}</p>)}
            </div>
            <div className="roi-agent-segment-tabs">
              {tabConfig.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  className={mockupTab === id ? "active" : ""}
                  onClick={() => setMockupTab(id)}
                >
                  <Icon size={12} /> {label}
                </button>
              ))}
            </div>
            {mockupTab === "pages" && (
              <div className="roi-agent-page-flow">
                {pages.length === 0 ? (
                  <p className="roi-agent-approval-empty">No pages in this mockup yet.</p>
                ) : (
                  pages.map((p, i) => (
                    <React.Fragment key={`${p.page_name}-${i}`}>
                      {i > 0 && <span className="roi-agent-page-arrow">→</span>}
                      <span
                        className={`roi-agent-page-badge${p.optional ? " optional" : ""}`}
                      >
                        {p.page_name}
                      </span>
                    </React.Fragment>
                  ))
                )}
              </div>
            )}
            {mockupTab === "fields" && (
              <ul className="roi-agent-field-list">
                {contentPages.flatMap((p) =>
                  (p.fields || []).map((f) => (
                    <li key={`${p.page_name}-${f.id}`}>
                      <span className="roi-agent-field-type">{f.field_type || "text"}</span>
                      <strong>{f.label || f.id}</strong>
                      <span className="roi-agent-approval-sub">({f.id})</span>
                    </li>
                  ))
                )}
                {!contentPages.some((p) => (p.fields || []).length) ? (
                  <li className="roi-agent-approval-empty-item">
                    No fields listed — ask the assistant to add inputs to the mockup.
                  </li>
                ) : null}
              </ul>
            )}
            {mockupTab === "formulas" && (
              <ul className="roi-agent-field-list roi-agent-formula-list">
                {formulaRules.length === 0 ? (
                  <li className="roi-agent-approval-empty-item">
                    No formulas yet. Ask the assistant to help define the calculation logic.
                  </li>
                ) : (
                  formulaRules.map((r, idx) => (
                    <li key={`${r.name}-${idx}`}>
                      <strong>{r.name}</strong>
                      {r.displayDetail || r.summary || r.formulaLine ? (
                        <span className="roi-agent-formula-summary">
                          {r.displayDetail || r.summary || r.formulaLine}
                        </span>
                      ) : (
                        <span className="roi-agent-formula-summary roi-agent-approval-empty-item">
                          Calculation steps are missing. Ask the assistant to complete this formula.
                        </span>
                      )}
                    </li>
                  ))
                )}
              </ul>
            )}
            {pendingMockup.edit && <div className="roi-agent-delivery-options"><p className="roi-agent-delivery-title">Changes to {pendingMockup.roi_name}</p><ul>{pendingMockup.edit.changes.map((change,index)=><li key={index}><strong>{change.property}</strong>: {String(change.before??'')} → {String(change.after??change.value)}</li>)}</ul></div>}
            {!pendingMockup.edit && <div className="roi-agent-delivery-options">
              <p className="roi-agent-delivery-title">Include in this calculator?</p>
              <label className="roi-agent-delivery-option">
                <input
                  type="checkbox"
                  checked={includeReportView}
                  onChange={(e) => setIncludeReportView(e.target.checked)}
                />
                <FileText size={16} aria-hidden />
                <span>
                  <strong>Report view</strong>
                  <small>Savings Report page visitors see after calculation</small>
                </span>
              </label>
              <label className="roi-agent-delivery-option">
                <input
                  type="checkbox"
                  checked={includePdfDownload}
                  onChange={(e) => setIncludePdfDownload(e.target.checked)}
                />
                <Download size={16} aria-hidden />
                <span>
                  <strong>PDF download</strong>
                  <small>Let visitors download a PDF copy of the report</small>
                </span>
              </label>
            </div>}
            <div className="roi-agent-mockup-actions">
              {pendingMockup.verification?.passed !== true && <p className="roi-agent-approval-blocker" role="status">{pendingMockup.verification?.logic_confirmation?.required ? "Confirm the proposed logic to continue. The draft will be verified before you approve the build." : "Approval is blocked because this draft has not passed QA. Select Run QA; resolve the errors shown above before building."}</p>}
              {buildError && <p role="alert" style={{color:'#b42318',width:'100%'}}>{buildError}</p>}
              {pendingMockup.verification?.logic_confirmation?.required ? <button type="button" className="roi-agent-btn-primary" disabled={loading} onClick={()=>sendMessage('Approve logic')}>Confirm logic</button> : pendingMockup.verification?.passed !== true && <button type="button" className="roi-agent-btn-primary" disabled={loading} onClick={verifyDraft}>Run QA</button>}
              <button type="button" className="roi-agent-btn-primary" title={pendingMockup.verification?.passed !== true?'QA must pass before approval':'Create the verified calculator'} disabled={loading || pendingMockup.verification?.passed !== true} onClick={() => approveBuild(true)}>
                <Check size={16} /> {building?(pendingMockup.edit?'Saving…':'Building…'):(pendingMockup.edit?'Approve & Save':'Approve & Build')}
              </button>
              <button type="button" className="roi-agent-btn-secondary" onClick={() => approveBuild(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const conversationStarted = messages.some((m) => m.role === "user");

  useEffect(() => {
    if (!open || conversationStarted) return;
    setPendingMockup(null);
  }, [open, conversationStarted]);

  const isWelcomeOnly =
    !conversationStarted &&
    messages.length === 1 &&
    messages[0]?.role === "assistant" &&
    !loading;

  if (!enabled) return null;

  const panelVisible = embedded || open;

  const panel = panelVisible ? (
    <div
      className={`roi-agent-panel${embedded ? " embedded" : ""}`}
      role="dialog"
      aria-label={PRODUCT_NAME}
    >
      <div className="roi-agent-header">
        <div className="roi-agent-header-main">
          <div className="roi-agent-avatar">
            <Bot size={20} />
          </div>
          <div className="roi-agent-header-text">
            <h3>{PRODUCT_NAME}</h3>
          </div>
        </div>
        <div className="roi-agent-header-actions">
          <button type="button" className="roi-agent-icon-btn" aria-label="New chat" title="New chat" onClick={clearChat}>
            <SquarePen size={18} />
          </button>
          {!embedded && (
            <button type="button" className="roi-agent-icon-btn" aria-label="Close assistant" title="Close assistant" onClick={() => setOpen(false)}>
              <X size={20} />
            </button>
          )}
        </div>
      </div>

      <div className="roi-agent-body">
        <div
          className={`roi-agent-messages${isWelcomeOnly ? " is-at-top" : " is-at-bottom"}`}
          ref={listRef}
          onScroll={onMessagesScroll}
        >
          <div className="roi-agent-messages-inner">
          {isWelcomeOnly && mode === 'builder' ? (
            <section className="roi-agent-welcome" aria-labelledby="roi-agent-welcome-title">
              <div className="roi-agent-welcome-icon" aria-hidden="true"><Calculator size={28} strokeWidth={1.6} /></div>
              <h2 id="roi-agent-welcome-title">What would you like to calculate?</h2>
              <p>Start with an idea. We’ll shape the logic together and create a calculator you can review.</p>
              <div className="roi-agent-starters">
                {quickChips.map(chip => <button key={chip.label} type="button" onClick={() => {setInput(chip.text);textareaRef.current?.focus();}}><span>{chip.label}</span><ArrowUpRight size={16} aria-hidden="true" /></button>)}
              </div>
              <p className="roi-agent-welcome-note">Have a workbook or design? Attach it anytime. Files are optional.</p>
            </section>
          ) : messages.map((msg, i) => (
            <div key={i} className={`roi-agent-msg-row ${msg.role}`}>
              <div className="roi-agent-avatar-sm">
                {msg.role === "assistant" ? (
                  <Bot size={14} />
                ) : (
                  userInitials(msg.user_name || userName)
                )}
              </div>
              <div className="roi-agent-msg-col">
                <div className={`roi-agent-msg ${msg.role}`}>
                  {msg.attachments?.length ? (
                    <div className="roi-agent-msg-files">
                      {msg.attachments.map((a) => (
                        <span key={a.filename} className="roi-agent-file-chip">
                          <Paperclip size={12} /> {a.filename}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <FormattedMessageContent content={msg.content} />
                  {msg.tool_calls?.length ? (
                    <details className="roi-agent-tool-details">
                    <summary>{msg.tool_outcomes?.some(outcome=>outcome.status==='error')?'Some steps need attention':'Steps taken'} <span>({msg.tool_calls.length})</span></summary>
                    <div className="roi-agent-tool-status-list">
                      {normalizeToolOutcomes(msg.tool_calls, msg.tool_outcomes).map(
                        (outcome, ti) => (
                          <div
                            key={`${outcome.name}-${ti}`}
                            className={toolStatusClass(outcome.status)}
                          >
                            {outcome.status === "error" ? (
                              <AlertCircle size={14} />
                            ) : outcome.status === "warn" ? (
                              <AlertCircle size={14} />
                            ) : (
                              <CheckCircle2 size={14} />
                            )}
                            {toolDisplayName(outcome.name, outcome)}
                          </div>
                        )
                      )}
                    </div>
                    </details>
                  ) : null}
                </div>
                {msg.role === "assistant" && msg.content ? (
                  <div className="roi-agent-msg-actions">
                    <button type="button" onClick={() => copyMessage(msg.content, i)}>
                      {copiedIndex === i ? <Check size={14} /> : <Copy size={14} />}
                      {copiedIndex === i ? "Copied" : "Copy"}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ))}

          {conversationStarted && pendingMockup ? renderApprovalCard() : null}

          {lastMetrics?.grand_total != null && (
            <div className="roi-agent-metrics-card">
              <strong>Latest metrics</strong>
              <div>Total / key metric: {String(lastMetrics.grand_total)}</div>
            </div>
          )}

          {loading && (
            <div className="roi-agent-msg-row assistant">
              <div className="roi-agent-avatar-sm">
                <Bot size={14} />
              </div>
              <div className="roi-agent-thinking" role="status" aria-live="polite">
                <span>{loadingHint || "Thinking…"}</span>
                <div className="roi-agent-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>
          )}
          </div>
        </div>

        <div className="roi-agent-footer">
          {!(isWelcomeOnly && mode==='builder') && <div className="roi-agent-chips">
            {quickChips.map((chip) => (
              <button key={chip.label} type="button" disabled={loading} onClick={() => {setInput(chip.text);textareaRef.current?.focus();}}>
                {chip.label}
              </button>
            ))}
          </div>}
          {pendingAttachments.length > 0 && (
            <div className="roi-agent-pending-files">
              {pendingAttachments.map((a) => (
                <span key={a.id} className="roi-agent-file-chip pending">
                  <Paperclip size={12} /> {a.file.name}
                  <button type="button" aria-label="Remove file" onClick={() => removePendingFile(a.id)}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_FILES}
            multiple
            hidden
            onChange={onPickFiles}
          />
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage(input);
            }}
          >
            <div className="roi-agent-composer-shell">
              <button
                type="button"
                className="roi-agent-attach-btn"
                aria-label="Attach logic or requirements file"
                title="Attach a workbook, document, or design (optional)"
                disabled={loading || pendingAttachments.length >= MAX_ATTACHMENTS}
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip size={18} />
              </button>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask anything…"
                aria-label="Message AI Calculator Assistant"
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    sendMessage(input);
                  }
                }}
              />
              <button
                type="submit"
                className={`roi-agent-send-btn ${
                  (input.trim() || pendingAttachments.length) && !loading ? "active" : "disabled"
                }`}
                disabled={loading || (!input.trim() && !pendingAttachments.length)}
                aria-label="Send message"
              >
                <ArrowUp size={18} />
              </button>
            </div>
          </form>
          <p className="roi-agent-disclaimer">
            Review formulas and assumptions before publishing.
          </p>
        </div>
      </div>
    </div>
  ) : null;

  if (embedded) return panel;

  return (
    <>
      {!open && (
        <div className="roi-agent-fab-wrap">
          <button
            type="button"
            className="roi-agent-fab"
            aria-label={`Open ${PRODUCT_NAME}`}
            onClick={() => setOpen(true)}
          >
            <Sparkles size={26} strokeWidth={2} />
          </button>
        </div>
      )}
      {panel}
    </>
  );
}
